// Screenshots of the Character Studio stage.
//
// Reviewing a character is a conversation: "the shoulder reads wrong from
// here" is only useful with a picture of "here". The stage had no way to
// produce one short of an OS screenshot cropped by hand.
//
// Capturing a WebGL canvas has two traps, and this handles both:
//
// - Without `preserveDrawingBuffer` (which the stage deliberately does not
//   set: it costs memory and speed on every frame), the browser may clear the
//   drawing buffer as soon as a frame is presented, so reading it later gives
//   a blank image. Rendering and then copying pixels in the same task, before
//   control returns to the browser, is always safe.
// - Under a `ViewportHost` the stage is one scissored rectangle of a canvas
//   shared with every other viewport on the page, so `gl.domElement.toBlob()`
//   would capture all of them. The stage is re-rendered into the bottom-left
//   corner of the drawing buffer at its own size and that rectangle copied
//   out; standalone, the rectangle is simply the whole canvas. The next frame
//   repaints everything, so the page never shows the difference.

import { Vector4, type Camera, type Scene, type WebGLRenderer } from 'three';

/** A rectangle of the drawing buffer, in device pixels from the top-left. */
export interface CaptureRegion {
  sx: number;
  sy: number;
  width: number;
  height: number;
}

/**
 * The device-pixel rectangle that a render at viewport `(0, 0, cssWidth,
 * cssHeight)` fills, measured from the top-left as `drawImage` expects.
 * WebGL viewports are anchored bottom-left, hence `sy`. Clamped to the buffer.
 */
export function bottomLeftCaptureRegion(
  bufferWidth: number,
  bufferHeight: number,
  cssWidth: number,
  cssHeight: number,
  pixelRatio: number,
): CaptureRegion {
  const ratio = Number.isFinite(pixelRatio) && pixelRatio > 0 ? pixelRatio : 1;
  const width = Math.max(1, Math.min(bufferWidth, Math.round(cssWidth * ratio)));
  const height = Math.max(1, Math.min(bufferHeight, Math.round(cssHeight * ratio)));
  return { sx: 0, sy: Math.max(0, bufferHeight - height), width, height };
}

/** `being-2026-09-14-12-30-05.png`: the character, then a sortable UTC timestamp. */
export function stageScreenshotFilename(characterId: string, date: Date = new Date()): string {
  const safe = characterId.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'character';
  const stamp = date.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `${safe}-${stamp}.png`;
}

/**
 * Render `scene` through `camera` at `size` (CSS pixels) and return it as a
 * PNG. Must be given the renderer state of the stage itself (inside a drei
 * `View`, the portal's own scene, camera and size). Restores the renderer's
 * viewport, scissor and auto-clear state before returning.
 */
export async function captureStagePng(
  gl: WebGLRenderer,
  scene: Scene,
  camera: Camera,
  size: { width: number; height: number },
): Promise<Blob> {
  const canvas = gl.domElement;
  const region = bottomLeftCaptureRegion(canvas.width, canvas.height, size.width, size.height, gl.getPixelRatio());
  const out = document.createElement('canvas');
  out.width = region.width;
  out.height = region.height;
  const context = out.getContext('2d');
  if (!context) throw new Error('this browser could not create a canvas for the image');

  const viewport = gl.getViewport(new Vector4());
  const scissor = gl.getScissor(new Vector4());
  const scissorTest = gl.getScissorTest();
  const autoClear = gl.autoClear;
  try {
    gl.autoClear = true;
    gl.setViewport(0, 0, size.width, size.height);
    gl.setScissor(0, 0, size.width, size.height);
    gl.setScissorTest(true);
    gl.render(scene, camera);
    // Same task as the render, so the drawing buffer is still intact.
    context.drawImage(canvas, region.sx, region.sy, region.width, region.height, 0, 0, region.width, region.height);
  } finally {
    gl.setViewport(viewport);
    gl.setScissor(scissor);
    gl.setScissorTest(scissorTest);
    gl.autoClear = autoClear;
  }

  return new Promise<Blob>((resolve, reject) => {
    out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('the browser returned no image data'))), 'image/png');
  });
}

/**
 * Hand `blob` to the browser as a file download. Some embedded or sandboxed
 * browsers block downloads without any error, which is why the studio also
 * offers copying to the clipboard where that is supported.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Long enough for any browser to have started the download.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** True when this browser can put an image on the clipboard. */
export function canCopyImageToClipboard(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function' &&
    typeof ClipboardItem !== 'undefined'
  );
}

/** Put a PNG on the clipboard. Rejects if the browser or the user refuses. */
export async function copyImageToClipboard(blob: Blob): Promise<void> {
  await navigator.clipboard.write([new ClipboardItem({ [blob.type || 'image/png']: blob })]);
}
